// features/artifact-library.js — ARTIFACT LIBRARY
// ═══════════════════════════════════════════════════════════
// Self-contained island. Owns: artifact list, filtering,
// search, pin, archive. Never owns: artifact creation logic.
// 3 tabs: Objects | Sessions | Journey
// Open button → 3-tab artifact session (stays inside Artifacts)
// ═══════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../hooks/use-i18n';
import ArtifactPanel from '../components/artifact-panel';
import ShiftPanelHeader from '../components/shift-panel-header';
import ShiftCard from '../components/shift-card';
import useSelection from '../hooks/use-selection';
import ThreadPickerModal from '../components/thread-picker-modal';
import ChatArtifactFeature from './chat-artifact-feature';
import ArtifactJourneyMap from './artifact-journey-map';

// Module-level flag — true after the first time ArtifactLibrary mounts on
// this page session. Resets to false on full page reload (module
// re-executes). Used to distinguish "user just refreshed" (restore the
// active session) from "user clicked the icon to come back" (show the
// panel). Same pattern as innovate-panel.js.
let hasInitiallyMounted = false;

const TYPE_COLORS = {
  web:      '#c8a96e',
  code:     '#c46050',
  document: '#9b8fc4',
  image:    '#7a9e82',
  data:     '#6b8fa8',
  upload:   '#b8956a',
};

export default function ArtifactLibrary({ userId, supabase, userRecord, onLoadThread, onArtifactNavigate, onClose }) {
  const { t } = useI18n();
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArtifact, setSelectedArtifact] = useState(null);

  // Artifact sessions
  const [artifactSessions, setArtifactSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Restore active session ONLY on the first mount after a page load
  // (user just refreshed). On subsequent mounts — including when the
  // user clicks the artifacts icon to come back — start fresh on the
  // panel. Matches innovate behavior.
  const [activeSession, setActiveSession] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (hasInitiallyMounted) return null;
    hasInitiallyMounted = true;
    try {
      const saved = sessionStorage.getItem('shift_artifact_active');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  }); // { sessionId, artifact }

  // Sync activeSession to sessionStorage so refresh restores it.
  // sessionStorage (not localStorage) — auto-clears when the tab closes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeSession) {
      try { sessionStorage.setItem('shift_artifact_active', JSON.stringify(activeSession)); } catch { /* quota exceeded */ }
    } else {
      sessionStorage.removeItem('shift_artifact_active');
    }
  }, [activeSession]);

  // UI state
  const [activeTab, setActiveTab] = useState('Objects');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const { selectMode, selectedItems, toggleSelectMode, toggleItem, clearSelection, isSelected } = useSelection();
  const typeRef = useRef(null);

  useEffect(() => {
    if (userId) {
      loadArtifacts();
      loadSessions();
    }
  }, [userId]);

  useEffect(() => {
    function handleClick(e) {
      if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
    }
    if (typeOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [typeOpen]);

  async function getAuthHeaders() {
    const { data: { session } } = await supabase.auth.getSession();
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token || ''}` };
  }

  async function loadArtifacts() {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const artRes = await fetch('/api/user/artifacts', { headers }).then(r => r.ok ? r.json() : { artifacts: [] }).catch(() => ({ artifacts: [] }));
      setArtifacts(artRes.artifacts || []);
    } catch (e) { console.error('loadArtifacts:', e); }
    setLoading(false);
  }

  async function loadSessions() {
    setSessionsLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/artifact-sessions', { headers });
      if (res.ok) {
        const { sessions } = await res.json();
        setArtifactSessions(sessions || []);
      }
    } catch (e) { console.error('loadSessions:', e); }
    setSessionsLoading(false);
  }

  // Open button — reuse existing session or create new
  async function handleOpenArtifact(a) {
    try {
      const headers = await getAuthHeaders();
      // Check if an artifact session already exists for this artifact
      const existing = artifactSessions.find(s => s.title === a.title);
      if (existing) {
        setActiveSession({ sessionId: existing.id, artifact: a });
        return;
      }
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({
          createNew: true,
          newTitle: a.title,
          description: a.description,
          items: [{ id: a.id, title: a.title, artifact_type: a.artifact_type, content: a.content, description: a.description }],
        }),
      });
      if (res.ok) {
        const { threadId } = await res.json();
        setActiveSession({ sessionId: threadId, artifact: a });
        loadSessions();
      }
    } catch (e) { console.error('handleOpenArtifact:', e); }
  }

  // Delete artifact session
  async function deleteSession(sessionId) {
    setArtifactSessions(prev => prev.filter(s => s.id !== sessionId));
    try {
      const headers = await getAuthHeaders();
      await fetch('/api/user/update', {
        method: 'POST', headers,
        body: JSON.stringify({ action: 'delete_thread', payload: { sessionId } }),
      });
    } catch (e) { console.error('deleteSession:', e); }
  }

  // + New thread from selection banner
  async function handleNewThread() {
    try {
      const headers = await getAuthHeaders();
      const title = selectedItems.length === 1
        ? selectedItems[0].title
        : `${selectedItems.length} items — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      const groupDesc = selectedItems.length === 1
        ? selectedItems[0].description
        : `${selectedItems.length} items: ${selectedItems.map(i => i.title).join(', ')}`;
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({
          createNew: true, newTitle: title, description: groupDesc,
          items: selectedItems.map(i => ({ id: i.id, title: i.title, artifact_type: i.artifact_type, content: i.content, description: i.description })),
        }),
      });
      if (res.ok) {
        const { threadId } = await res.json();
        clearSelection();
        // Open the session viewer with the first selected artifact
        setActiveSession({ sessionId: threadId, artifact: selectedItems[0] });
      }
    } catch (e) { console.error('handleNewThread:', e); }
  }

  // Send to existing thread via picker
  async function handlePickerSend(thread) {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch('/api/user/send-to-thread', {
        method: 'POST', headers,
        body: JSON.stringify({
          threadId: thread.id,
          items: selectedItems.map(i => ({ id: i.id, title: i.title, artifact_type: i.artifact_type, content: i.content, description: i.description })),
        }),
      });
      if (res.ok) {
        const { threadId } = await res.json();
        clearSelection();
        setShowPicker(false);
      }
    } catch (e) { console.error('handlePickerSend:', e); }
  }

  // ── FILTERED DATA ──────────────────────────────────────────
  const pinned = artifacts.filter(a => a.is_pinned);
  const unpinned = artifacts.filter(a => !a.is_pinned);

  const filteredArtifacts = (list) => {
    if (!typeFilter) return list;
    return list.filter(a => (a.artifact_type || '').toLowerCase() === typeFilter.toLowerCase());
  };

  const searchFilter = (list) => {
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(a => (a.title || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q));
  };

  const filteredSessions = artifactSessions.filter(s =>
    !searchQuery || (s.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── CARD PROPS ──────────────────────────────────────────
  function cardProps(a) {
    return {
      key: a.id,
      item: { id: a.id, title: a.title, created_at: a.created_at, summary_bullets: a.summary_bullets || [], session_title: a.session_title || null },
      accentColor: TYPE_COLORS[(a.artifact_type || '').toLowerCase()] || '#888',
      typeLabel: a.artifact_type || 'artifact',
      preview: a.description,
      hidePreview: true,
      onOpen: () => (onArtifactNavigate ? onArtifactNavigate(a) : handleOpenArtifact(a)),
      openLabel: 'View →',
      onDelete: async () => {
        setArtifacts(prev => prev.filter(x => x.id !== a.id));
        const headers = await getAuthHeaders();
        fetch('/api/user/update', { method: 'POST', headers, body: JSON.stringify({ action: 'archive_artifact', payload: { artifactId: a.id } }) }).catch(() => {});
      },
      selectMode,
      isSelected: isSelected(a.id),
      onSelect: () => toggleItem(a),
    };
  }

  // ── ACTIVE SESSION VIEW (3-tab chat) ──────────────────────
  if (activeSession) {
    return (
      <div className="shift-panel-overlay" style={{ display: 'flex', flexDirection: 'column' }}>
        <ChatArtifactFeature
          userId={userId} supabase={supabase} userRecord={userRecord}
          artifact={activeSession.artifact}
          sessionId={activeSession.sessionId}
          onBack={() => { setActiveSession(null); loadSessions(); }}
        />
      </div>
    );
  }

  return (
    <div className="shift-panel-overlay">

      {/* ── 1. HEADER ── */}
      <ShiftPanelHeader
        title="Artifacts"
        tagline="every artifact shift has built"
        taglineInline={true}
        tabs={[
          { label: 'Objects', color: '#c8a96e' },
          { label: 'Sessions', color: '#7a9e82' },
          { label: 'Journey', color: '#9b8fc4' },
        ]}
        activeTab={activeTab}
        onTabChange={(tab) => { setActiveTab(tab); clearSelection(); setSearchQuery(''); setTypeFilter(''); }}
        onClose={onClose}
      />

      <div className="panel-content">

        {/* ── 2. TOOLBAR ── */}
        {activeTab !== 'Journey' && (
          <div className="cards-toolbar" style={{ borderBottom: '1px solid var(--border-1)', background: 'var(--surface-1)', flexShrink: 0 }}>
            <div className="cards-search-wrap">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="6.5" cy="6.5" r="5" stroke="var(--text-5)" strokeWidth="1.4"/>
                <path d="M10.5 10.5L14 14" stroke="var(--text-5)" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              <input type="text" className="cards-search"
                placeholder={activeTab === 'Sessions' ? 'Search sessions...' : 'Search objects...'}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onFocus={e => e.target.style.borderColor = 'var(--gold)'}
                onBlur={e => e.target.style.borderColor = 'var(--border-2)'} />
            </div>
            <div className="cards-toolbar-right">
              {activeTab === 'Objects' && <button className={`cards-toolbar-select${selectMode ? ' active' : ''}`} onClick={toggleSelectMode}>Select</button>}
              {activeTab === 'Objects' && (() => {
                const selectedTypeColor = typeFilter ? TYPE_COLORS[typeFilter.toLowerCase()] : '#888888';
                return (
                  <div ref={typeRef} style={{ position: 'relative' }}>
                    <button className="cards-toolbar-dropdown" onClick={() => setTypeOpen(!typeOpen)}
                      style={{ color: selectedTypeColor, borderColor: typeFilter ? selectedTypeColor : 'var(--border-2)' }}>
                      {typeFilter || 'All types'}
                      <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 1l3 3 3-3"/></svg>
                    </button>
                    {typeOpen && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 4, zIndex: 50, minWidth: 130 }}>
                        {['All types', 'Web', 'Code', 'Document', 'Image', 'Data', 'Upload'].map(t => {
                          const itemColor = t === 'All types' ? '#888888' : TYPE_COLORS[t.toLowerCase()];
                          const isActive = typeFilter === t || (t === 'All types' && !typeFilter);
                          return (
                            <div key={t} onClick={() => { setTypeFilter(t === 'All types' ? '' : t); setTypeOpen(false); }}
                              style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: isActive ? itemColor : '#888888', fontFamily: 'Georgia, serif', transition: 'color 0.15s', borderLeft: isActive ? `2px solid ${itemColor}` : '2px solid transparent' }}>
                              {t !== 'All types' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: itemColor, flexShrink: 0 }} />}
                              {t}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── 3. SELECTION BANNER ── */}
        {selectMode && selectedItems.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: 'rgba(122,158,130,0.05)', borderBottom: '1px solid rgba(122,158,130,0.15)', flexShrink: 0 }}>
            <div style={{ flex: 1, fontSize: 12, color: 'var(--sage)', fontFamily: 'Georgia, serif' }}>
              {selectedItems.length} {selectedItems.length === 1 ? 'item' : 'items'} selected
            </div>
            <button onClick={handleNewThread} style={{ background: 'transparent', border: '1px solid rgba(122,158,130,0.5)', color: 'var(--sage)', fontSize: 11, fontFamily: 'Georgia, serif', padding: '4px 12px', borderRadius: 3, cursor: 'pointer' }}>+ New thread</button>
            <button onClick={() => setShowPicker(true)} style={{ background: 'transparent', border: '1px solid var(--border-2)', color: 'var(--text-5)', fontSize: 11, fontFamily: 'Georgia, serif', padding: '4px 12px', borderRadius: 3, cursor: 'pointer' }}>Send to thread</button>
            <button
              onClick={clearSelection}
              onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-6)'}
              style={{ background: 'none', border: 'none', color: 'var(--text-6)', cursor: 'pointer', fontSize: 11, fontFamily: 'Georgia, serif', transition: 'color 0.15s' }}
            >Cancel</button>
          </div>
        )}

        {/* ── 4. CONTENT AREA ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {loading && <div style={{ color: 'var(--text-7)', fontSize: 13, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>loading···</div>}

          {/* ═══ OBJECTS TAB ═══ */}
          {!loading && activeTab === 'Objects' && (
            <>
              {searchFilter(filteredArtifacts(artifacts)).length === 0 && (
                <div style={{ color: 'var(--text-7)', fontSize: 13, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>
                  {searchQuery ? 'No artifacts match your search.' : 'No artifacts yet. Shift will save them here automatically.'}
                </div>
              )}
              {searchFilter(filteredArtifacts(pinned)).length > 0 && (
                <>
                  <div style={{ color: 'var(--text-7)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'var(--font-serif)', marginBottom: 12 }}>Pinned</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 400px))', gap: '40px', justifyContent: 'center', marginBottom: 24 }}>
                    {searchFilter(filteredArtifacts(pinned)).map(a => <ShiftCard {...cardProps(a)} />)}
                  </div>
                </>
              )}
              {searchFilter(filteredArtifacts(unpinned)).length > 0 && (
                <>
                  {searchFilter(filteredArtifacts(pinned)).length > 0 && (
                    <div style={{ color: 'var(--text-7)', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'var(--font-serif)', marginBottom: 12 }}>Recent</div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 400px))', gap: '40px', justifyContent: 'center', marginBottom: 24 }}>
                    {searchFilter(filteredArtifacts(unpinned)).map(a => <ShiftCard {...cardProps(a)} />)}
                  </div>
                </>
              )}
            </>
          )}

          {/* ═══ SESSIONS TAB ═══ */}
          {!loading && activeTab === 'Sessions' && (
            <>
              {sessionsLoading && <div style={{ color: 'var(--text-7)', fontSize: 13, fontFamily: 'var(--font-serif)', textAlign: 'center', padding: '40px 0', fontStyle: 'italic' }}>loading···</div>}
              {!sessionsLoading && filteredSessions.length === 0 && (
                <div style={{ color: 'var(--text-6)', fontSize: 13, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', padding: 40 }}>
                  {searchQuery ? 'No sessions match your search.' : 'Open an artifact to start a session here.'}
                </div>
              )}
              {!sessionsLoading && filteredSessions.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 400px))', gap: '40px', justifyContent: 'center' }}>
                  {filteredSessions.map(s => {
                    // Match accent color to source artifact type
                    const sourceArtifact = artifacts.find(a => a.title === s.title);
                    const sourceType = (sourceArtifact?.artifact_type || '').toLowerCase();
                    const isGroup = (s.title || '').includes('items —');
                    const accentColor = isGroup ? '#888' : (TYPE_COLORS[sourceType] || '#c8a96e');
                    const typeLabel = isGroup ? 'Collection' : (sourceArtifact?.artifact_type || 'Session');
                    return (
                    <ShiftCard key={s.id}
                      item={{ id: s.id, title: s.title || 'Untitled session', created_at: s.created_at, summary_bullets: sourceArtifact?.summary_bullets || [] }}
                      accentColor={accentColor}
                      typeLabel={typeLabel}
                      preview={s.what_shifted || sourceArtifact?.description}
                      actionLabel="Resume →"
                      onAction={() => setActiveSession({ sessionId: s.id, artifact: sourceArtifact || { title: s.title, artifact_type: 'Session' } })}
                      onDelete={() => deleteSession(s.id)}
                    />
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ═══ JOURNEY TAB ═══ */}
          {!loading && activeTab === 'Journey' && (
            <ArtifactJourneyMap
              userId={userId}
              supabase={supabase}
              artifacts={artifacts}
              onArtifactNavigate={onArtifactNavigate}
            />
          )}
        </div>
      </div>

      {/* ── VIEWER MODAL ── */}
      {selectedArtifact && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSelectedArtifact(null)}>
          <div style={{ width: '90%', maxWidth: 900, height: '80vh', background: '#07070a', borderRadius: 12, border: '1px solid #2a2a2a', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}>
            <ArtifactPanel artifact={selectedArtifact} onClose={() => setSelectedArtifact(null)} session={null} supabase={supabase} />
          </div>
        </div>
      )}

      {/* ── THREAD PICKER MODAL ── */}
      <ThreadPickerModal isOpen={showPicker} onClose={() => setShowPicker(false)} onSend={handlePickerSend} selectedCount={selectedItems.length} supabase={supabase} />
    </div>
  );
}
